<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getDatasets, getDashboard, getDashboardPartners } from '../lib/api.js';
  import { mayVisit, session } from '../lib/session.svelte.js';
  import { HELP } from '../lib/help.js';
  import { amount, periodRange } from '../lib/datasets.js';

  // Where things stand: what we have and share, what partners share back, and
  // what we compare. How it is made is under Advanced (docs/ref-belonging-plan.md,
  // step 3).
  async function load() {
    const [{ datasets }, dashboard] = await Promise.all([getDatasets(), getDashboard()]);
    return { datasets, freshness: dashboard.freshness };
  }
  const status = load();

  const showPartners = $derived(mayVisit('partners'));
  const showAnalysis = $derived(mayVisit('analysis'));
  // Remote health is requested on its own, so the local summary shows first.
  const partnerHealth = $derived(showPartners ? getDashboardPartners() : null);
</script>

<HelpDrawer {...HELP.overview} />

<h1 class="wf2-title wf2-title-level-1">Overview</h1>
<p class="wf2-subtitle">Signed in as <strong>{session.user?.name}</strong>. What we have, what we share and
  what we compare.</p>

<Async promise={status}>
  {#snippet children({ datasets, freshness })}
    <!-- Nothing collected yet: no dataset, or only ones prepared in the seed
         whose file has not been uploaded (docs/ref-belonging-plan.md, 4.6). -->
    {@const nothingYet = datasets.every((d) => d.sources.every((s) => s.waitingForFile))}
    {@const waiting = datasets.filter((d) => d.sources.some((s) => s.waitingForFile))}
    {#if freshness.state !== 'current' && !nothingYet}
      <div class="state error">
        The amounts are {freshness.state}.
        {#if mayVisit('sources')}Rebuild them under <a href="#/advanced">Advanced</a>.{:else}Ask a data steward to rebuild them.{/if}
      </div>
    {/if}
    <div class="wf2-row">
      <div class="wf2-col wf2-flexitem">
        <Section title="Data">
          <Panel>
            {#if nothingYet}
              <p class="wf2-text"><strong>Nothing has been collected yet.</strong></p>
              {#if mayVisit('dataset/new')}
                <p class="wf2-text">
                  Start from a file: an export of a survey, registrations or ticket
                  sales, as CSV. You choose which columns to keep and how each one
                  is shared, and the dataset, its terms and its first counts are
                  made from it.
                </p>
                <div class="wf2-row" style="gap:12px">
                  <Button variant="primary" to="dataset/new">Start from a file</Button>
                </div>
                {#each waiting as d}
                  {#each d.sources.filter((s) => s.waitingForFile) as s}
                    <p class="wf2-text">
                      Or upload the file for <strong>{d.label}</strong>, which is set up
                      and waiting for it{d.sources.length > 1 ? ` (${s.label})` : ''}.
                    </p>
                    <div class="wf2-row" style="gap:12px">
                      <Button variant="secondary" to={`source/ingest?id=${encodeURIComponent(s.id)}`}>Upload its file</Button>
                    </div>
                  {/each}
                {/each}
              {:else}
                <p class="wf2-text">A data steward starts a dataset from a file.</p>
              {/if}
            {:else if datasets.length}
              <!-- Each dataset opens its own page: what it holds, its terms, who gets what. -->
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Dataset</th><th>How much</th><th>Periods</th><th>Shared with</th></tr></thead>
                <tbody>
                  {#each datasets as d}
                    <tr>
                      <td><a href={`#/data/dataset?id=${encodeURIComponent(d.id)}`}><strong>{d.label}</strong></a></td>
                      <td>{amount(d)}</td>
                      <td>{periodRange(d.periods)}</td>
                      <td>{d.sharing.partners.map((p) => p.label).join(', ') || 'nobody yet'}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
            {/if}
          </Panel>
        </Section>
      </div>

      {#if showPartners || showAnalysis}
      <div class="wf2-col wf2-flexitem">
        {#if showPartners}
        <Section title="Partners">
          <Panel>
            <Async promise={partnerHealth} loading="Checking partner connections…">
              {#snippet children(remote)}
                <p class="wf2-subtitle">{remote.partners.length} in the registry · checked {new Date(remote.observedAt).toLocaleTimeString()}</p>
                <DataTable
                  columns={['Partner', 'They share', 'Status']}
                  rows={remote.partners.map((p) => [
                    p.label,
                    p.inboundCount ? `${p.readableCount} of ${p.inboundCount} readable` : 'nothing shared yet',
                    p.readableCount ? 'Sharing both ways' : p.inboundCount ? 'Waiting on them' : 'You share, they don’t',
                  ])}
                />
              {/snippet}
            </Async>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="partners">Manage partners</Button>
              <Button variant="secondary" to="partner">Add partner</Button>
            </div>
          </Panel>
        </Section>
        {/if}

        {#if showAnalysis}
        <Section title="Comparisons">
          <Panel>
            <p class="wf2-text">
              Each institution's figures side by side. You and your partners
              record things like gender in your own words — the comparison keeps
              both, rather than making anyone change theirs.
            </p>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="analysis">View comparisons</Button>
              <Button variant="secondary" to="queries">New comparison</Button>
            </div>
          </Panel>
        </Section>
        {/if}
      </div>
      {/if}
    </div>
  {/snippet}
</Async>
